import {
  FileStorageContext,
  getCompletedUploadsOfCategory,
} from '@rabbit/bizproc/react';
import { UserUploadedDocument } from '@rabbit/data/types';
import { UploadedFileCategories } from '@rabbit/elements/shared-types';
import { useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export interface SageFileUploadAutoUpdaterProps {
  fileCategory: UploadedFileCategories;
}

export function OliveFileUploadAutoUpdater({
  fileCategory,
}: SageFileUploadAutoUpdaterProps) {
  const { t } = useTranslation();
  const fileStorageContext = useContext(FileStorageContext);
  const {
    uploadQueueState,
    moveCompletedUploadsToAttached,
    updateHoldingWithFiles,
  } = fileStorageContext || {};

  useEffect(() => {
    if (
      !moveCompletedUploadsToAttached ||
      !updateHoldingWithFiles ||
      !uploadQueueState?.ongoing ||
      !uploadQueueState?.completed
    )
      return;

    /* -------------------------------------------------------------------------- */
    /*                     Autosave consumer proof of purchase                    */
    /* -------------------------------------------------------------------------- */

    // Wait until all uploads are complete before we do anything
    const { ongoing, completed } = uploadQueueState;
    if (ongoing.length === 0 && completed.length !== 0) {
      // todo make switch
      if (fileCategory === UploadedFileCategories.ConsumerProofPurchase) {
        // Consumer proofs of purchase
        const proofOfPurchaseUploads = getCompletedUploadsOfCategory(
          completed,
          UploadedFileCategories.ConsumerProofPurchase
        );

        if (proofOfPurchaseUploads.length === 0) return;

        const uploadedFiles = proofOfPurchaseUploads.map(
          (item) => item.uploadedFile
        );

        // We know that all the files are for the same holding, so we can just take the first docid
        updateHoldingWithFiles(
          proofOfPurchaseUploads?.[0].docType?.docid ?? '',
          uploadedFiles as UserUploadedDocument[],
          UploadedFileCategories.ConsumerProofPurchase
        )
          .then(() => {
            moveCompletedUploadsToAttached(proofOfPurchaseUploads);
          })
          .catch((err) => {
            // Swallowing this left the file listed as uploaded while nothing
            // referenced it, so the user walked away believing it was saved.
            console.error(err);
            toast.error(
              t(
                "Couldn't attach the uploaded file, please try uploading it again."
              )
            );
          });
      }
      if (fileCategory === UploadedFileCategories.SerialNumberProof) {
        // Consumer proofs of purchase
        const serialProofUploads = getCompletedUploadsOfCategory(
          completed,
          UploadedFileCategories.SerialNumberProof
        );

        if (serialProofUploads.length === 0) return;

        const uploadedFiles = serialProofUploads.map(
          (item) => item.uploadedFile
        );

        // We know that all the files are for the same holding, so we can just take the first docid
        updateHoldingWithFiles(
          serialProofUploads?.[0].docType?.docid ?? '',
          uploadedFiles as UserUploadedDocument[],
          UploadedFileCategories.SerialNumberProof
        )
          .then(() => {
            moveCompletedUploadsToAttached(serialProofUploads);
          })
          .catch((err) => {
            // Swallowing this left the file listed as uploaded while nothing
            // referenced it, so the user walked away believing it was saved.
            console.error(err);
            toast.error(
              t(
                "Couldn't attach the uploaded file, please try uploading it again."
              )
            );
          });
      }
    }
  }, [uploadQueueState?.ongoing, uploadQueueState?.completed]);

  return null;
}
